import { DateRadioChipGroup, FormField, Heading, RadioChipOptions, Select, Text } from '@expressable/syntax-ds';
import { format } from 'date-fns';
import { EvaluationTimeSlot } from 'domains/calendars/v3/useEvaluationCalendarSummary';
import { UseEvaluationCalendarResult } from 'domains/signupDirect/misc/useSignupEvaluationCalendar';
import { TimezoneOption, timeZoneOptions } from 'domains/timezone';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import { isEmpty } from 'lodash';
import { FC, useEffect, useMemo, useState } from 'react';
import { useController, UseControllerReturn, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PageSizeConfig } from './CalendarPageConfig';
import { DateChipAdornment } from './DateChipAdornment';
import { TherapistSummaryProps } from 'components/ContentfulTherapistCard/ContentfulTherapistSummary';
import { EvaluationScheduleFormType } from 'domains/signupDirect/schemas/evaluationScheduleSchema';
import { TherapistAvailabilityCard } from './TherapistAvailabilityCard';
import { TimeOptionsCheckboxes } from 'components/TimeOptionsCheckboxes/TimeOptionsCheckboxes';
import { NoTimeOptionsMatches } from 'components/TimeOptionsCheckboxes/NoTimeOptionsMatches';
import { useTimeOptionsFilter } from 'domains/signupDirect/Evaluation/Schedule/TimeOptions/hooks/useTimeOptionsFilter';

type Props = {
  onChange?: (timeSlot?: EvaluationTimeSlot) => void;
  name: string;
  calendarData: Extract<UseEvaluationCalendarResult, { isLoading: false }>;
  therapistBadgesData: Omit<TherapistSummaryProps, 'therapistName'>;
  shouldShowTimeOptions?: boolean;
};

// Experimental component, currently only meant to be used in single page signup. Will be rewritten and ported to syntax

const DAY_PICKER_ID = 'dayPickerInput';

export const EvaluationCalendarPicker: FC<Props> = ({
  onChange,
  name,
  calendarData,
  therapistBadgesData,
  shouldShowTimeOptions = false,
}) => {
  // @TODO(juan): types need to be cleaned up here, and properly separate the component from RHF schema
  const {
    field,
    fieldState: { error },
  } = useController({ name: name as 'timeSlot' }) as UseControllerReturn<
    Partial<EvaluationScheduleFormType>,
    'timeSlot'
  >;
  const { isSubmitting } = useFormState();

  const { currentAvailability: currentTimelineSlots, dayIndex, onChangeDay } = calendarData;

  const { availabilityByTherapist, day: currentDay } = currentTimelineSlots;

  const { displayTimezone, setDisplayTimezone, format: formatInTimezone } = useDisplayTimezone();

  const { filteredAvailabilityByTherapist, handleTimeOptionsChange, noSlotsMatchTimeFilters } = useTimeOptionsFilter({
    availabilityByTherapist,
    displayTimezone,
    enabled: shouldShowTimeOptions,
  });

  const dayOptions: RadioChipOptions = useMemo(() => {
    return calendarData.allTimeSlots
      .map(({ day, therapistCount, timeSlotCount }, index) => {
        return {
          value: `${index}`,
          date: day,
          adornment: <DateChipAdornment count={therapistCount} disabled={timeSlotCount === 0} />,
          disabled: timeSlotCount === 0,
        };
      })
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [calendarData]);

  const onChangeTimeSlot = (slot?: EvaluationTimeSlot) => {
    onChange?.(slot);
    field.onChange(slot);
  };

  const { t } = useTranslation();

  // Define if there are no available slots (depends on the timelineSlots and if all requests are resolved)
  const noAvailableSlots = availabilityByTherapist.length === 0;

  const hasAvailableSlots = !noAvailableSlots && !noSlotsMatchTimeFilters;

  // Start day carousel on first day with available slots
  const [startIndex, setStartIndex] = useState<number | undefined>();

  useEffect(() => {
    const dayOptionIndex = dayOptions.findIndex(option => !option.disabled);

    if (dayOptionIndex !== -1 && dayIndex < dayOptionIndex) {
      onChangeDay(dayOptionIndex);
      setStartIndex(dayOptionIndex);
    }
  }, [dayOptions, dayIndex, onChangeDay]);

  const GhostAnchor = () => <span ref={field.ref} className="hidden" />;

  return (
    <>
      <FormField label={t('directSignup.schedule.timezone.label')} name={!displayTimezone ? name : undefined}>
        <Select
          options={timeZoneOptions}
          onChange={selectedValue => {
            if (isEmpty(selectedValue)) {
              onChangeTimeSlot(undefined);
              setDisplayTimezone(undefined);
            } else {
              setDisplayTimezone(timeZoneOptions.find(({ value }) => value === selectedValue) as TimezoneOption);
            }
          }}
          value={displayTimezone?.value}
          disabled={isSubmitting}
          standalone
        />
      </FormField>

      {!displayTimezone && (
        <Text variant="h6">
          {t('directSignup.schedule.noTimezone')} <GhostAnchor />
        </Text>
      )}

      {displayTimezone && (
        <>
          {/* @TODO(juan): this formField is a workaround until syntax supports hiding/pointing errors to specific inputs dynamically */}

          <FormField
            name={noAvailableSlots ? name : undefined}
            label={t('directSignup.schedule.dayPicker.label')}
            config={{ order: { error: 'top', hint: 'top' } }}
          >
            {/* @TODO temporary solution until component supports ID prop and manual focusing */}
            <div id={DAY_PICKER_ID} tabIndex={-1} />
            <DateRadioChipGroup
              htmlName="daypicker"
              chips={dayOptions}
              variant="carousel"
              pageSize={PageSizeConfig}
              value={`${dayIndex}`}
              startIndex={startIndex}
              onChange={e => {
                onChangeTimeSlot(undefined);
                onChangeDay(parseInt(e.target.value, 10));
              }}
              disabled={isSubmitting}
              dateFormatFn={format}
              standalone
            />
          </FormField>

          {noAvailableSlots && (
            <Text variant="h6">
              {t('directSignup.schedule.noSlots')} <GhostAnchor />
            </Text>
          )}

          {shouldShowTimeOptions && (
            <TimeOptionsCheckboxes name="timeOptions" onTimeOptionsChange={handleTimeOptionsChange} />
          )}

          {noSlotsMatchTimeFilters && (
            <>
              <NoTimeOptionsMatches
                onTimeOptionsChange={handleTimeOptionsChange}
                availableTherapistsCount={availabilityByTherapist.length}
                currentDay={format(currentDay, 'EEEE, MMM d')}
              />

              {availabilityByTherapist.map((therapistAvailability, index) => (
                <TherapistAvailabilityCard
                  key={therapistAvailability.therapist.therapistID}
                  therapistAvailability={therapistAvailability}
                  index={index}
                  therapistBadgesData={therapistBadgesData}
                  currentDay={currentDay}
                  formFieldName={name}
                  formatInTimezone={formatInTimezone}
                  onChangeTimeSlot={onChangeTimeSlot}
                  isSubmitting={isSubmitting}
                  selectedSlotId={field.value?.id}
                  error={error}
                />
              ))}
            </>
          )}

          {hasAvailableSlots && (
            <div className="flex flex-col gap-e24">
              <Heading level="h4" noDefaultMargins>
                {t('directSignup.schedule.availabilityTitle', { date: format(currentDay, 'EEEE, MMMM d') })}
              </Heading>

              {filteredAvailabilityByTherapist.map((therapistAvailability, index) => (
                <TherapistAvailabilityCard
                  key={therapistAvailability.therapist.therapistID}
                  therapistAvailability={therapistAvailability}
                  index={index}
                  therapistBadgesData={therapistBadgesData}
                  currentDay={currentDay}
                  formFieldName={name}
                  formatInTimezone={formatInTimezone}
                  onChangeTimeSlot={onChangeTimeSlot}
                  isSubmitting={isSubmitting}
                  selectedSlotId={field.value?.id}
                  error={error}
                />
              ))}
            </div>
          )}

          <GhostAnchor />
        </>
      )}
    </>
  );
};
